import React, { useState } from "react";
// UI Framework
import { Menu, MenuItem, Tooltip } from "@mui/material";
// Common Components
import Button from "src/Components/Button";
import DatePicker from "src/Components/DatePicker";
import FullScreenLoading from "src/Components/Loading/FullScreenLoading";
import { TimePicker, Modal } from "src/Components";
import { WrappedTimeSeriesChart } from "src/Components/Timeseries/WrappedTimeSeriesChart";
import Cell from "src/Tables/TableTemplate//Cell";
import CircularProgress from "@mui/material/CircularProgress";
// Requests
import {
  getGainAfterCommissionDailyGraph,
  getCreditAvailabilityGraph,
  getNonCreditAvailabilityGraph,
  getLivePairPositionGraph,
  getLivePairPositionGraphDaily,
  getPositionPriceTargetProfitPositionGraph,
  getRollDailyRollGainGraph,
  getRollDailyRollGainGraphDaily,
  getRollDailyRollGainCompoundGraph,
  getRollPairPositionGraph,
  getRollPairPositionGraphDaily,
  getDailyGainOffsetGraph,
  getDailyGainOffsetGraphDaily,
  getPackagePriceGraph,
  getPackagePriceGraphDaily,
  getGainAfterCommissionCompoundYearlyGraph,
  getGainAfterCommissionCompoundYearlyGraphDaily,
  getGainAfterCommissionDailyGraphDaily,
  getTimeValueNetGainYearlyCompoundGraph,
  getTimeValueNetGainYearlyCompoundGraphDaily,
  getRollCompundRollTimeDecayGraph,
  getRollCompundRollTimeDecayGraphDaily,
  getRollDailyRollGainCompoundGraphDaily,
} from "src/Requests/coveredCall";
// Utilities
import { packetErrorHandler } from "src/Utils/requestUtils";
import { getCoveredCallChartMarketHours } from "src/Utils/coveredCallChartMarketHours";
import moment from "moment";
// Types
import { Graph_Dto } from "src/Interfaces/orderbookGraphInterfaces";
import {
  IChartCommonSearchParam,
  ICoveredCallBuyingPowerGraphParams,
  IDailyChartSearchParams,
} from "src/Interfaces/coveredCall";
// Local Components
import SmallEnterPairPositionDailyChartUI from "./SmallEnterPairPositionDailyChartUI";
import EnterPairPositionDailyChartUI from "./EnterPairPosition/DailyChartUI";
import RollDailyChartUI from "./RollCalculator/DailyChartUI";
import SmallRollDailyChartUI from "./SmallRollDailyChartUI";

const TradeLegsOverlay = ({
  headerName,
  row,
  tradeLegs,
}: Pick<Graph_Dto, "tradeLegs"> & {
  headerName: string;
  row: Record<string, unknown>;
}) => {
  if (!tradeLegs?.length) return null;

  const isOffsetChart =
    (headerName === "DailyGainOffset" || headerName === "PackagePrice") &&
    typeof row.buySide1 !== "string" &&
    typeof row.sellSide1 !== "string";

  const rollInstrumentIds = Array.isArray(row.insIds) ? row.insIds : [];
  const tableLegs = [
    {
      instrumentId:
        row.buyInstrumentId1 ?? row.instrumentId1 ?? rollInstrumentIds[0],
      instrumentName:
        row.buyInstrumentName1 ?? row.sourceInstrumentName ?? row.baseInstrument,
      side: row.buySide1 ?? row.sourceOrderSide,
    },
    {
      instrumentId: row.buyInstrumentId2 ?? row.instrumentId2,
      instrumentName: row.buyInstrumentName2,
      side: row.buySide2,
    },
    {
      instrumentId:
        row.sellInstrumentId1 ?? row.instrumentId3 ?? rollInstrumentIds[1],
      instrumentName:
        row.sellInstrumentName1 ?? row.destInstrumentName ?? row.baseInstrumentType,
      side: row.sellSide1 ?? row.destOrderSide,
    },
    {
      instrumentId: row.sellInstrumentId2 ?? row.instrumentId4,
      instrumentName: row.sellInstrumentName2,
      side: row.sellSide2,
    },
  ].filter(
    (
      leg,
    ): leg is {
      instrumentId: string;
      instrumentName: unknown;
      side: unknown;
    } => typeof leg.instrumentId === "string" && leg.instrumentId.length > 0,
  );
  const tableLegsByInstrumentId = new Map(
    tableLegs.map((leg) => [leg.instrumentId, leg]),
  );

  const getDirection = (side: unknown, fallback: "Buy" | "Sell") => {
    if (typeof side !== "string") return fallback;
    if (side === "Buy" || side.includes("خرید")) return "Buy";
    if (side === "Sell" || side.includes("فروش")) return "Sell";
    return fallback;
  };

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-center gap-1 px-2.5 py-1 text-xs">
      {tradeLegs.map((tradeLeg, index) => {
        const tableLeg =
          tableLegsByInstrumentId.get(tradeLeg.instrumentId) ??
          tableLegs[index];
        const direction = isOffsetChart
          ? tradeLeg.direction
          : getDirection(tableLeg?.side, tradeLeg.direction);

        return (
          <span key={`${direction}-${tradeLeg.instrumentId}`}>
            <span
              className={
                direction === "Buy" ? "text-emerald-600" : "text-red-600"
              }
            >
              {direction === "Buy" ? "خرید" : "فروش"}
            </span>{" "}
            <span className="text-slate-600">
              {typeof tableLeg?.instrumentName === "string"
                ? tableLeg.instrumentName
                : tradeLeg.instrumentName}
            </span>
          </span>
        );
      })}
    </div>
  );
};

interface HourlyChartUIProps {
  formValues: IChartCommonSearchParam;
  timeISOValues: { fromTime: string | null; toTime: string | null };
  onChange: (name: string, value: any) => void;
  onTimeChange: (
    name: string,
    value: { timeString: string; ISOString: string | null },
  ) => void;
  onSubmit: () => void;
  headerName: string;
  row: Record<string, unknown>;
  graphData: Graph_Dto;
  loading: boolean;
}

const HourlyChartUI = React.memo(
  ({
    formValues,
    timeISOValues,
    onChange,
    onTimeChange,
    onSubmit,
    headerName,
    row,
    graphData,
    loading,
  }: HourlyChartUIProps) => {
    const dataset = React.useMemo(() => [graphData], [graphData]);

    return (
      <div className="flex flex-row gap-3 justify-between items-start w-full h-full min-h-0">
        <div className="flex flex-col w-52 shrink-0 justify-start items-start gap-3 top-14">
          <DatePicker
            label="تاریخ"
            name="date"
            onChange={onChange}
            value={formValues.date}
          />
          <TimePicker
            name="fromTime"
            value={timeISOValues.fromTime}
            onChange={onTimeChange}
            placeholder="از ساعت"
          />
          <TimePicker
            name="toTime"
            value={timeISOValues.toTime}
            onChange={onTimeChange}
            placeholder="تا ساعت"
          />
          <Button text="جستجو" onClick={onSubmit} variant="contained" />
        </div>
        <div className="flex h-full w-full min-h-0 min-w-0 flex-1 flex-col">
          <TradeLegsOverlay
            headerName={headerName}
            row={row}
            tradeLegs={graphData.tradeLegs}
          />
          {loading ? (
            <div className="flex justify-center items-center h-full w-full">
              <CircularProgress />
            </div>
          ) : (
            <div className="min-h-0 min-w-0 flex-1">
              <WrappedTimeSeriesChart
                dataset={dataset}
                YValueType="priceWithDecimal"
                withDate={false}
                className="h-full w-full"
              />
            </div>
          )}
        </div>
      </div>
    );
  },
);

const ChartSearchParamUI = ({
  cellType,
  headerName,
  row,
  trigger,
  translateHeaderName,
}: any) => {
  const openerTitle =
    typeof trigger?.props?.title === "string" ? trigger.props.title : "";
  const dockTitle = openerTitle || translateHeaderName || headerName;
  const marketHours = getCoveredCallChartMarketHours(row.key);
  const isLivePairPosition = headerName === "LivePairPosition";
  const isRollPairPosition = headerName === "RollPairPosition";
  const enterPairPositionDailyChartGetter =
    getEnterPairPositionDailyChartGetter(headerName);
  const rollDailyChartGetter = getRollDailyChartGetter(headerName);
  const hasDailyChart =
    !!enterPairPositionDailyChartGetter || !!rollDailyChartGetter;
  const displayCellType =
    hasDailyChart && headerName !== "PackagePrice" ? "Ltr" : "Price";
  const showCombinedChart =
    isLivePairPosition || isRollPairPosition || hasDailyChart;

  // local store
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [loading, setLoading] = React.useState(false);
  const [graphData, setGraphData] = React.useState<Graph_Dto>({} as Graph_Dto);
  const [formValues, setFormValues] = React.useState<IChartCommonSearchParam>({
    fromTime: marketHours.fromTime,
    toTime: marketHours.toTime,
    date: moment().format("YYYY-MM-DD"),
    key: row.key,
  } as IChartCommonSearchParam);

  const [timeISOValues, setTimeISOValues] = React.useState<{
    fromTime: string | null;
    toTime: string | null;
  }>({
    fromTime: getFromTimeISODefaultValue(),
    toTime: getToTimeISODefaultValue(),
  });

  React.useEffect(() => {
    if (!isModalOpen) return;
    setAnchorEl(null);
    setIsTooltipOpen(false);
    onSubmit();
  }, [isModalOpen]);

  function getFromTimeISODefaultValue() {
    const defaultFromTime = new Date();
    const [hours, minutes] = marketHours.fromTime.split(":").map(Number);
    defaultFromTime.setHours(hours, minutes, 0, 0);

    return defaultFromTime.toISOString();
  }
  function getToTimeISODefaultValue() {
    const defaultToTime = new Date();
    const [hours, minutes] = marketHours.toTime.split(":").map(Number);
    defaultToTime.setHours(hours, minutes, 0, 0);
    return defaultToTime.toISOString();
  }

  const properFunction = () => {
    const data: IChartCommonSearchParam = {
      fromTime: formValues.fromTime,
      toTime: formValues.toTime,
      date: formValues.date,
      key: row.key,
    };
    const buyingPowerData: ICoveredCallBuyingPowerGraphParams = {
      coveredCallOrderId: Number(row.id),
      fromTime: formValues.fromTime,
      toTime: formValues.toTime,
      date: formValues.date,
    };
    switch (headerName) {
      case "CreditAvailability":
        return getCreditAvailabilityGraph(buyingPowerData);
      case "NonCreditAvailability":
        return getNonCreditAvailabilityGraph(buyingPowerData);
      case "GainAfterCommissionDaily":
        return getGainAfterCommissionDailyGraph(data);
      case "GainAfterCommissionCompoundYearly":
        return getGainAfterCommissionCompoundYearlyGraph(data);
      case "TimeValueNetGainYearlyCompound":
        return getTimeValueNetGainYearlyCompoundGraph(data);
      case "LivePairPosition":
        return getLivePairPositionGraph(data);
      case "PositionPrice_TargetProfitPosition":
        return getPositionPriceTargetProfitPositionGraph(data);
      case "DailyRollGain":
        return getRollDailyRollGainGraph(data);
      case "MonthlyRollGain":
        return getMonthlyRollGainGraph(data);
      case "YearlyRollGain":
        return getYearlyRollGainGraph(data);
      case "DailyRollGainCompound":
        return getRollDailyRollGainCompoundGraph(data);
      case "CompundRollTimeDecay":
        return getRollCompundRollTimeDecayGraph(data);
      case "RollPairPosition":
        return getRollPairPositionGraph(data);
      case "DailyGainOffset":
        return getDailyGainOffsetGraph(data);
      case "PackagePrice":
        return getPackagePriceGraph(data);

      default:
        return getPackagePriceGraph(data);
    }
  };

  const changeHandler = (name: string, value: any) => {
    setFormValues({ ...formValues, [name]: value });
  };

  const timePickerChangeHandler = (
    name: string,
    value: {
      timeString: string;
      ISOString: string | null;
    },
  ) => {
    setTimeISOValues({ ...timeISOValues, [name]: value.ISOString });
    setFormValues({ ...formValues, [name]: value.timeString });
  };

  const onSubmit = () => {
    setLoading(true);
    properFunction()
      .then((resp) => setGraphData(resp))
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
    setAnchorEl(null);
    setIsTooltipOpen(false);
  };

  const rightClickHandler = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMenuOpen(true);
    setAnchorEl(e.currentTarget);
  };

  const getChartHandler = (event?: React.SyntheticEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    handleCloseMenu();
    setIsModalOpen(true);
  };

  const hourlyChartUI = (
    <HourlyChartUI
      formValues={formValues}
      timeISOValues={timeISOValues}
      onChange={changeHandler}
      onTimeChange={timePickerChangeHandler}
      onSubmit={onSubmit}
      headerName={headerName}
      row={row}
      graphData={graphData}
      loading={loading}
    />
  );

  const ModalUI = () => {
    let chartContent: React.ReactNode;

    if (showCombinedChart) {
      chartContent = (
        <div className="flex h-full w-full min-h-0 flex-col justify-between overflow-hidden">
          <div className="h-1/2 min-h-0 w-full shrink-0 overflow-hidden">
            {hourlyChartUI}
          </div>
          <div className="h-1/2 min-h-0 w-full shrink-0 overflow-hidden ">
            {isLivePairPosition ? (
              <SmallEnterPairPositionDailyChartUI
                row={row}
                chartGetter={getLivePairPositionGraphDaily}
              />
            ) : isRollPairPosition ? (
              <SmallRollDailyChartUI
                row={row}
                chartGetter={getRollPairPositionGraphDaily}
              />
            ) : enterPairPositionDailyChartGetter ? (
              <EnterPairPositionDailyChartUI
                row={row}
                chartGetter={enterPairPositionDailyChartGetter}
              />
            ) : (
              <RollDailyChartUI row={row} chartGetter={rollDailyChartGetter!} />
            )}
          </div>
        </div>
      );
    } else if (enterPairPositionDailyChartGetter) {
      chartContent = (
        <EnterPairPositionDailyChartUI
          row={row}
          chartGetter={enterPairPositionDailyChartGetter}
        />
      );
    } else if (rollDailyChartGetter) {
      chartContent = (
        <RollDailyChartUI row={row} chartGetter={rollDailyChartGetter} />
      );
    } else {
      chartContent = (
        <div className="flex h-full w-full flex-col gap-2">{hourlyChartUI}</div>
      );
    }

    return (
      <div className="relative h-full w-full min-h-0 overflow-hidden">
        <div className="h-full min-h-0">{chartContent}</div>
      </div>
    );
  };

  return (
    <>
      {isModalOpen ? (
        <Modal
          onClose={() => {
            handleCloseMenu();
            setIsModalOpen(false);
          }}
          fullScreen
          title={dockTitle}
          UI={ModalUI()}
          modalBoxClassName="!z-[9999] covered-call-chart-modal"
        />
      ) : (
        <></>
      )}
      {trigger ? (
        React.cloneElement(trigger, { onClick: getChartHandler })
      ) : (
        <Tooltip
          title="نمودار"
          className="!w-full !relative"
          open={isTooltipOpen}
          onMouseEnter={() => setIsTooltipOpen(true)}
          onMouseLeave={() => setIsTooltipOpen(false)}
        >
          <div onContextMenu={rightClickHandler} className="ltr cursor-pointer">
            <Cell
              clonedRow={row}
              headerName={headerName}
              type={displayCellType}
            />
            {isMenuOpen ? (
              <Menu
                className="!z-[9998]"
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={handleCloseMenu}
                MenuListProps={{
                  "aria-labelledby": "basic-button",
                }}
              >
                <MenuItem onClick={getChartHandler}>نمودار</MenuItem>
              </Menu>
            ) : (
              <></>
            )}
          </div>
        </Tooltip>
      )}
    </>
  );
};

const getEnterPairPositionDailyChartGetter = (headerName: string) => {
  switch (headerName) {
    case "GainAfterCommissionCompoundYearly":
      return getGainAfterCommissionCompoundYearlyGraphDaily;
    case "GainAfterCommissionDaily":
      return getGainAfterCommissionDailyGraphDaily;
    case "TimeValueNetGainYearlyCompound":
      return getTimeValueNetGainYearlyCompoundGraphDaily;
    case "DailyGainOffset":
      return getDailyGainOffsetGraphDaily;
    case "PackagePrice":
      return getPackagePriceGraphDaily;
    default:
      return null;
  }
};

const getRollDailyChartGetter = (headerName: string) => {
  switch (headerName) {
    case "DailyRollGain":
      return getRollDailyRollGainGraphDaily;
    case "MonthlyRollGain":
      return getMonthlyRollGainGraphDaily;
    case "YearlyRollGain":
      return getYearlyRollGainGraphDaily;
    case "DailyRollGainCompound":
      return getRollDailyRollGainCompoundGraphDaily;
    case "CompundRollTimeDecay":
      return getRollCompundRollTimeDecayGraphDaily;
    default:
      return null;
  }
};

const getMonthlyRollGainGraph = (params: IChartCommonSearchParam) =>
  getRollDailyRollGainGraph(params).then(toMonthlyRollGainGraph);

const getMonthlyRollGainGraphDaily = (params: IDailyChartSearchParams) =>
  getRollDailyRollGainGraphDaily(params).then(toMonthlyRollGainGraph);

const getYearlyRollGainGraph = (params: IChartCommonSearchParam) =>
  getRollDailyRollGainGraph(params).then(toYearlyRollGainGraph);

const getYearlyRollGainGraphDaily = (params: IDailyChartSearchParams) =>
  getRollDailyRollGainGraphDaily(params).then(toYearlyRollGainGraph);

const toMonthlyRollGainGraph = (graph: Graph_Dto): Graph_Dto => ({
  ...graph,
  name: "درصد بازده ماهانه رول",
  graphPoints: graph.graphPoints.map((point) => ({
    ...point,
    price: point.price === null ? null : point.price * 30,
  })),
});

const toYearlyRollGainGraph = (graph: Graph_Dto): Graph_Dto => ({
  ...graph,
  name: "درصد بازده سالانه ساده رول",
  graphPoints: graph.graphPoints.map((point) => ({
    ...point,
    price: point.price === null ? null : point.price * 365,
  })),
});

export default ChartSearchParamUI;
